import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSession, useUser } from "@clerk/clerk-react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { DownloadSimple, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import {
  getZipJobStatus,
  downloadZipJobFile,
  ZipJobStatus,
} from "shared/api/zip-download-job";

/**
 * Landing page opened from the "ZIP staat klaar" notification. It downloads the
 * generated archive using the active Clerk session and shows progress/errors.
 * While the job is still building it polls until ready, so the page also works
 * if the user opens the link before the notification's full message lands.
 */
const POLL_INTERVAL_MS = 3000;

const ZipDownloadPage = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const { session } = useSession();
  const { user } = useUser();

  const [status, setStatus] = useState<ZipJobStatus | "unknown">("unknown");
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval>>();
  const autoTriggered = useRef(false);

  const triggerDownload = useCallback(async () => {
    if (!session || !user?.id || !jobId) return;
    setIsDownloading(true);
    setError(null);
    try {
      await downloadZipJobFile(Number(jobId), session, user.id);
      setHasDownloaded(true);
    } catch (err) {
      console.error("Error downloading ZIP:", err);
      setError(err instanceof Error ? err.message : "Download mislukt");
    } finally {
      setIsDownloading(false);
    }
  }, [session, user?.id, jobId]);

  // Poll job status until it is completed or failed; auto-download once ready.
  useEffect(() => {
    if (!session || !jobId) return;

    let cancelled = false;

    const check = async () => {
      try {
        const job = await getZipJobStatus(Number(jobId), session);
        if (cancelled) return;
        setStatus(job.status);

        if (job.status === "completed") {
          if (pollRef.current) clearInterval(pollRef.current);
          if (!autoTriggered.current) {
            autoTriggered.current = true;
            triggerDownload();
          }
        } else if (job.status === "failed") {
          if (pollRef.current) clearInterval(pollRef.current);
          setError(job.error || "Deze download kon niet worden aangemaakt");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching ZIP job status:", err);
        setError(err instanceof Error ? err.message : "Kon downloadstatus niet ophalen");
        if (pollRef.current) clearInterval(pollRef.current);
      }
    };

    check();
    pollRef.current = setInterval(check, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [session, jobId, triggerDownload]);

  const isPreparing = status === "pending" || status === "processing" || status === "unknown";

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Download voorbereiden</title>
        </Helmet>
      </HelmetProvider>

      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-lg border border-neutral-primary/10 bg-white p-8 text-center shadow-sm">
          {error ? (
            <>
              <WarningCircle size={48} weight="duotone" className="text-status-bad" />
              <h1 className="text-lg font-medium text-neutral-primary">Download niet beschikbaar</h1>
              <p className="text-sm text-neutral-secondary-mid">{error}</p>
            </>
          ) : isPreparing && !hasDownloaded ? (
            <>
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-moss-primary border-t-transparent" />
              <h1 className="text-lg font-medium text-neutral-primary">Je download wordt voorbereid…</h1>
              <p className="text-sm text-neutral-secondary-mid">
                Dit kan even duren bij veel bestanden. Je kan deze pagina open laten of er later
                naartoe terugkeren via de melding.
              </p>
            </>
          ) : (
            <>
              <CheckCircle size={48} weight="duotone" className="text-status-good" />
              <h1 className="text-lg font-medium text-neutral-primary">Je ZIP staat klaar</h1>
              <p className="text-sm text-neutral-secondary-mid">
                {hasDownloaded
                  ? "De download is gestart. Niet begonnen? Klik hieronder om opnieuw te downloaden."
                  : "Klik hieronder om je bestanden te downloaden."}
              </p>
              <ButtonNew
                variant="primaryMoss"
                icon={<DownloadSimple size={16} />}
                label={isDownloading ? "Downloaden…" : "Download ZIP"}
                onClick={triggerDownload}
                isDisabled={isDownloading}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ZipDownloadPage;
